import axios from 'axios';

/**
 * Axios instance used everywhere the SPA talks to the PHP backend.
 *
 *   - withCredentials = true → PHP session cookie (used by captcha) is sent
 *     on every request.
 *   - In dev,  baseURL = '/backend'   (Vite proxies to XAMPP)
 *   - In prod, baseURL = window.location.origin + '/backend'
 */

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '/backend';

/** Printable receipt opened in a new tab after a successful Finder search. */
export function finderReceiptUrl(token) {
  const root = String(baseURL).replace(/\/$/, '');
  return `${root}/api/finder/receipt?token=${encodeURIComponent(token)}`;
}

/** ZIP of the Paper ID's certificate folder after a successful Finder search. */
export function finderPackUrl(token) {
  const root = String(baseURL).replace(/\/$/, '');
  return `${root}/api/finder/pack?token=${encodeURIComponent(token)}`;
}

export const api = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
});

// FormData must keep the browser-generated multipart boundary. Forcing
// `Content-Type: multipart/form-data` without that boundary makes PHP drop
// every field (empty $_POST / $_FILES).
api.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    if (config.headers && typeof config.headers.delete === 'function') {
      config.headers.delete('Content-Type');
    } else if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }
  return config;
});

function userMessageFromError(err) {
  const data = err.response?.data;
  const raw =
    (data && typeof data === 'object' && typeof data.message === 'string' && data.message) ||
    (typeof data === 'string' ? data : '') ||
    '';
  if (/imunify360/i.test(raw) || /bot-protection/i.test(raw)) {
    return 'The host firewall blocked this request. In cPanel open Imunify360 → Incidents, whitelist it, then try again.';
  }
  if (raw && typeof data === 'object') {
    return raw;
  }
  return err.message || 'Something went wrong. Please try again.';
}

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Normalise validation errors so React Hook Form can surface them. This
    // covers 422 (validation) and other statuses that still carry per-field
    // errors, e.g. 413 when an upload exceeds the server's size limit.
    if (err.response?.data?.errors && typeof err.response.data.errors === 'object') {
      err.fieldErrors = err.response.data.errors;
    }
    err.userMessage = userMessageFromError(err);
    return Promise.reject(err);
  },
);

/* ─────────────── Typed endpoint wrappers ─────────────── */

export const endpoints = {
  health:              ()                  => api.get('/'),
  captcha:             ()                  => api.get('/api/captcha'),

  contactSubmit:       (data)              => api.post('/api/contact/submit', data),
  joinUsSubmit:        (formData)          => api.post('/api/join-us/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  feedbackSubmit:      (formData)          => api.post('/api/feedback/submit', formData),
  registrationSubmit:  (data)              => api.post('/api/registration/submit', data),
  registrationBilling: (paperId)           => api.get('/api/registration/billing', { params: { paper_id: paperId } }),
  registrationCategoryByToken: ({ vId, pId }) => api.get('/api/registration/category-by-token', { params: { v_id: vId, p_id: pId } }),
  registrationOfferByCode: (id)            => api.get('/api/registration/offer-by-code', { params: { id } }),
  registrationOfferSubmit: (data)          => api.post('/api/registration/offer-submit', data),
  // Reports a checkout the author abandoned / cancelled at the gateway, which
  // neither CCAvenue nor PayPal calls back about.
  registrationPaymentStatus: (data)        => api.post('/api/registration/payment-status', data),

  // PayPal — modern Orders v2 (Smart Buttons) flow.
  paypalConfig:        ()                  => api.get('/api/payment/paypal-config'),
  paypalCreateOrder:   (data)              => api.post('/api/registration/paypal/create', data),
  paypalCaptureOrder:  (data)              => api.post('/api/registration/paypal/capture', data),
  attendanceSubmit:    (formData)          => api.post('/api/attendance/submit', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),

  participationOptions: ()                 => api.get('/api/participation/options'),
  participationSubmit: (formData)          => api.post('/api/participation/submit', formData, { timeout: 120_000 }),

  testimonialsList:    ()                  => api.get('/api/testimonials/list'),
  testimonialSubmit:   (data)              => api.post('/api/testimonials/submit', data),

  finderSearch:        (data)              => api.post('/api/finder/search', data),

  paymentLookup:       (orderId)           => api.get('/api/payment/registration', { params: { order_id: orderId } }),
  ccavenueResponse:    (encResp)           => api.post('/api/payment/ccavenue-response', { encResp }),

  publications:        (year)              => api.get('/api/content/publications', { params: year ? { year } : {} }),
  speakers:            (year)              => api.get('/api/content/speakers',     { params: year ? { year } : {} }),
  committees:          (slug)              => api.get('/api/content/committees',   { params: slug ? { slug } : {} }),
  dates:               ()                  => api.get('/api/content/dates'),
  venue:               ()                  => api.get('/api/content/venue'),
  gallery:             ()                  => api.get('/api/content/gallery'),
  tracks:              ()                  => api.get('/api/content/tracks'),
  indexing:            ()                  => api.get('/api/content/indexing'),
  // Admin-editable page-content block (returns the stored JSON value or null).
  contentBlock:        (key)              => api.get('/api/content/block', { params: { key } }),
};
